import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { queryKeys } from "../lib/queryKeys";
import { getApiErrorMessage } from "../lib/apiErrors";
import type { TicketArticleLinksResponse, TicketArticleLinkResponse, ArticleTicketLinksResponse, ArticleLinkType } from "../types/articles";
import type { ArticleDto } from "../types/articles";

export function useTicketArticleLinks(ticketId: string | undefined, organizationId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.tickets.articleLinks(ticketId!), organizationId],
    queryFn: async () => {
      const response = await apiService.getTicketArticleLinks(ticketId!, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch article links'));
      }
      return response.data as TicketArticleLinksResponse;
    },
    enabled: !!ticketId && !!organizationId,
    staleTime: 30000,
  });
}

export function useLinkArticleToTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, request, organizationId }: {
      ticketId: string;
      request: { articleId: string; linkType: ArticleLinkType; notes?: string };
      organizationId?: string;
    }) => {
      const response = await apiService.linkArticleToTicket(ticketId, request, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to link article'));
      }
      return response.data as TicketArticleLinkResponse;
    },
    onSuccess: (_, { ticketId, request }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.articleLinks(ticketId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
      // The article's own list of linked tickets
      queryClient.invalidateQueries({ queryKey: queryKeys.articles.ticketLinks(request.articleId) });
    },
  });
}

export function useUnlinkArticleFromTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, articleId, organizationId }: {
      ticketId: string;
      articleId: string;
      organizationId?: string;
    }) => {
      const response = await apiService.unlinkArticleFromTicket(ticketId, articleId, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to unlink article'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId, articleId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.articleLinks(ticketId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
      // The article's own list of linked tickets
      queryClient.invalidateQueries({ queryKey: queryKeys.articles.ticketLinks(articleId) });
    },
  });
}

export function useUpdateTicketArticleLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, articleId, request, organizationId }: {
      ticketId: string;
      articleId: string;
      request: { linkType: ArticleLinkType; notes?: string };
      organizationId?: string;
    }) => {
      const response = await apiService.updateTicketArticleLink(ticketId, articleId, request, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to update article link'));
      }
      return response.data as TicketArticleLinkResponse;
    },
    onSuccess: (_, { ticketId, articleId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.articleLinks(ticketId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.articles.ticketLinks(articleId) });
    },
  });
}

export function useSuggestArticlesForTicket(
  ticketId: string | undefined,
  query: string,
  organizationId: string | undefined,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: [...queryKeys.tickets.articleSuggest(ticketId!, query), organizationId],
    queryFn: async () => {
      const response = await apiService.suggestArticlesForTicket(ticketId!, query, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to suggest articles'));
      }
      return response.data as ArticleDto[];
    },
    enabled: enabled && !!ticketId && !!organizationId,
    staleTime: 10000,
    // The previous results stay up while the next search runs, instead of flashing a spinner
    placeholderData: keepPreviousData,
  });
}

export function useArticleTicketLinks(articleId: string | undefined, organizationId?: string) {
  return useQuery({
    queryKey: [...queryKeys.articles.ticketLinks(articleId!), organizationId],
    queryFn: async () => {
      const response = await apiService.getArticleTicketLinks(articleId!, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch ticket links'));
      }
      return response.data as ArticleTicketLinksResponse;
    },
    enabled: !!articleId,
    staleTime: 30000,
  });
}
